import L, { LatLngBounds, LatLngExpression } from 'leaflet';

import React, { useContext, useEffect, useMemo, useRef } from 'react';
import {
  MapContainer,
  ScaleControl,
  useMap,
  useMapEvents
} from "react-leaflet";

import { FullscreenControl } from "react-leaflet-fullscreen";
import "react-leaflet-fullscreen/styles.css";

import { Box, Typography } from '@mui/material';
import { AccountContext } from '../context/account';
import { MapDataContext, detaultMapCenter } from '../context/map-data';
import { Bounds, MapLocation } from '../core/global';
import { retrieveItem, storeItem } from '../core/storage';
import { LayerDescription } from '../data/layers';
import { ActiveLayer, GridLayer } from './active-layer';
import { ExportButton } from './export-xyz';
import { CurrentMap } from './layers-toggle';
import { MapPopUpModal } from './map-popup-modal';
import { MapProjectPoints } from './map-project-points';
import { MouseCoordinates } from './mouse-coordinates';
import { OnDemandsLayers } from './on-demand-layers';
import { PreviewButton } from './preview-button';
import { SelectedAreaLayer } from './selected-area-layer';
import { ValidationPoints } from './validation-points';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, pl: 0, pr: 0 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const convertBoundsToLatLngBounds = (bounds: Bounds): LatLngBounds => {
  const southWest = L.latLng([Number(bounds.min.lat), Number(bounds.min.lng)]);
  const northEast = L.latLng([Number(bounds.max.lat), Number(bounds.max.lng)]);
  return new LatLngBounds(southWest, northEast);
};

interface Props {
  width: number
  height: number
  center: MapLocation
  open: boolean
}

function ChangeLocationDescendant({
  width,
  height,
  center,
  open,
}: Props) {
  const map = useMap();
  const {location, bounds} = center

  useEffect(() => {
  
    if (map && location) {
      if (bounds) {
        const latLngBounds = convertBoundsToLatLngBounds(bounds);
        const currentBounds = map.getBounds()
        const alreadyZoomed = latLngBounds.equals(currentBounds)
        
        if (!alreadyZoomed) {
          map.fitBounds(latLngBounds, {
            maxZoom: 9
          });
        }
      } else {
        const zoomLevel = map.getZoom()
        map.setView([Number(location.lat), Number(location.lng)], zoomLevel > 9 ? zoomLevel : 9, {animate: false})
        // const xOffset = width / 2;
        // const yOffset = height / 2;
        // map.panBy([Math.round(385/2), 0], { animate: false });
        // map.panBy([0, 0], { animate: false });
        if (open) {
          map.panBy([Math.round(385/2)-28, 0], { animate: false });
        }
      }
    }
  }, [map, JSON.stringify(center), open])
  
  return null
}

interface MapBoundsUpdateDescendantProps {
  onChange: (lat: number, lon: number) => void
  onChangeMapLocation: (lat: number, lon: number, bbox?: Bounds | null, zoom?: number | null) => void
  suppressNextMapClickRef?: React.MutableRefObject<boolean>
}

export function MapBoundsUpdateDescendant({
  onChange,
  onChangeMapLocation,
  suppressNextMapClickRef,
}: MapBoundsUpdateDescendantProps & {
  suppressNextMapClickRef?: React.MutableRefObject<boolean>
}) {
  const map = useMap()

  useMapEvents({
    moveend: () => {
      const center = map.getCenter()
      const bounds = map.getBounds()
      const zoom = map.getZoom()

      if (!center) return

      onChangeMapLocation(
        center.lat,
        center.lng,
        {
          min: {
            lat: bounds.getSouth(),
            lng: bounds.getWest(),
          },
          max: {
            lat: bounds.getNorth(),
            lng: bounds.getEast(),
          }
        } as Bounds,
        zoom
      )
    },

    click: (event: any) => {
      if (suppressNextMapClickRef?.current) {
        suppressNextMapClickRef.current = false
        return
      }

      const tag = (event.originalEvent.target?.tagName || '').toLowerCase()

      if (tag !== 'button') {
        onChange(Number(event.latlng.lat), Number(event.latlng.lng))
      }
    },
  })

  return null
}

interface StyledMapContainerProps {
  open: boolean
}

const StyledMapContainer = ({
  open,
}: StyledMapContainerProps) => {
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const [containerHeigth, setContainerHeigth] = React.useState<number>(0);

  const { 
    updateMeshData, 
    mapCenter,
    dataSources,
    dataSourceModel,
    currentLayer,
    showModelGrid,
    zoom,
    setMapCenter,
    setCurrentMapCenter,
  } = useContext(MapDataContext);

  const { 
    account,
  } = useContext(AccountContext);

  const suppressNextMapClickRef = useRef(false)

  const position: LatLngExpression = detaultMapCenter;
  
  const ref = useRef<HTMLDivElement | null>(null);


  useEffect(() => {
    window.setTimeout(() => {
      if (ref && ref.current) {
        const width = ref.current.clientWidth
        setContainerWidth(width)
        const height = ref.current.clientHeight
        setContainerHeigth(height)
      }
    }, 700)
    return () => {
      ref.current = null;
    }
  }, [ref, open])

  const modelGridLayer: LayerDescription | null = useMemo(() => {
      if (dataSourceModel && dataSources) {
        const {key} = dataSourceModel
        const { layers } = dataSources[key] || []
        if (layers && layers.length > 0) {
          const modelGrid = layers.find(i => i.valueKey === 'model-grid')
          return modelGrid || null
        }
      }
      return null
  }, [JSON.stringify(dataSourceModel), JSON.stringify(dataSources)])

  const vectorLayerUrl: string | null = useMemo(() => {
    if (dataSourceModel && dataSources) {
      const {key} = dataSourceModel
      const { vector_tiles } = dataSources[key] || []
      return vector_tiles || null
    }
    return null
}, [JSON.stringify(dataSourceModel), JSON.stringify(dataSources)])

const canExportLayerData = useMemo(() => {
  if (account) {
    const {loggedIn, organisation} = account
    if (loggedIn && organisation && Array.isArray(organisation)) {
      let canExport = false
      for (const org of organisation) {
        if (org?.features && org?.features?.can_export_layers) {
          canExport = true
          break
        }
      }
      return canExport
    }
  }
  return false
}, [account])

if (typeof MapContainer === "undefined" || typeof window === 'undefined' || typeof document === 'undefined') {
  return null
}

  return (
    <div id='map-container'>
      <MapContainer
        style={{ width: "100%", height: "calc(100vh - 56px)" }}
        center={position}
        zoom={6}
        minZoom={2}
        scrollWheelZoom={true}
        maxZoom={Number(import.meta.env.VITE_MAX_ZOOM || 11)}
      >
        <PreviewButton />
        {canExportLayerData && <ExportButton />}
        <OnDemandsLayers />
        <CurrentMap />
        <ScaleControl position="bottomleft" />
        <MouseCoordinates />
        <FullscreenControl  />
        {currentLayer && <ActiveLayer currentLayer={currentLayer} vectorLayer={vectorLayerUrl} />}
        {showModelGrid && zoom > 2 && modelGridLayer && <GridLayer currentLayer={modelGridLayer} />}
        <SelectedAreaLayer suppressNextMapClickRef={suppressNextMapClickRef} />
        {mapCenter && <ChangeLocationDescendant open={open} height={containerHeigth} width={containerWidth} center={mapCenter} />}
        <MapPopUpModal 
          onClose={() => {}}
        />
        <MapProjectPoints />
        <ValidationPoints />
        <MapBoundsUpdateDescendant
          suppressNextMapClickRef={suppressNextMapClickRef}
          onChange={(lat, lon) => {
            const coords = `${lat},${lon}`
            const savedLastLocation: any = retrieveItem('map-location') || {}
            storeItem('map-location', {
              ...savedLastLocation,
              lat: Number(lat),
              lon: Number(lon),
              drawerOpen: open,
            })
            updateMeshData({coords})
          }}
          onChangeMapLocation={(lat, lon, bounds) => {
            setCurrentMapCenter({
              location: {
                lat: Number(lat), 
                lng: Number(lon)
              },
              bounds: bounds || undefined
            })
          }}
        />
      </MapContainer>
    </div>
  );
};

export default StyledMapContainer;
